import { useEffect, useRef, useState } from "react";

/**
 * Browser Web Speech API wrapper. Returns:
 *   { supported, listening, transcript, toggle, reset }
 * Transcript is the cumulative, finalised text.
 */
export const useVoiceTranscription = (initial = "") => {
  const [transcript, setTranscript] = useState(initial);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const finalRef = useRef(initial);
  const supported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    if (!supported) return undefined;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        const text = r[0].transcript;
        if (r.isFinal) {
          finalRef.current = (finalRef.current + " " + text).trim();
        } else {
          interim += text;
        }
      }
      setTranscript((finalRef.current + " " + interim).trim());
    };

    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);

    recognitionRef.current = rec;
    return () => {
      try {
        rec.stop();
      } catch (_) {
        /* noop */
      }
    };
  }, [supported]);

  const toggle = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      try {
        rec.stop();
      } catch (_) {
        /* noop */
      }
      setListening(false);
    } else {
      try {
        rec.start();
        setListening(true);
      } catch (_) {
        /* noop */
      }
    }
  };

  const reset = () => {
    finalRef.current = "";
    setTranscript("");
  };

  return { supported, listening, transcript, toggle, reset };
};
